import React from 'react';
import { twMerge } from 'tailwind-merge';
/**
 * CTable - shared, reusable data table.
 *
 * A white rounded card wrapping a horizontally-scrollable table with a grey
 * header row and hover-highlighted body rows. Fully data-driven via `columns` +
 * `data`, with built-in loading and empty states so it can serve any list page.
 *
 * Styling follows the attendance pages (`px-4 py-3`, sentence-case headers,
 * `text-sm` inherited from the table) rather than the older uppercase
 * `tracking-wider` look, which is the chosen house style. `whitespace-nowrap`
 * on cells is deliberately retained: it is layout behaviour, not style, and
 * removing it globally would rewrap long content on every list page. Headers
 * carry it for the same reason: without it a two-word header ("Employment
 * Type") wrapped onto two lines and heightened the header row while its own
 * column's cells never wrapped. The cells already size each column to their
 * widest unwrapped value, so a short header cannot overflow.
 *
 * Classes are composed with `twMerge`, not string concatenation. Two Tailwind
 * utilities of the same specificity in one class attribute are resolved by CSS
 * source order, not attribute order, so a plain concatenation left every
 * caller override silently dead: `text-gray-900` is emitted after
 * `text-gray-500` and `whitespace-nowrap` after `whitespace-normal`, so
 * UserManagement's Description column could not opt into wrapping or grey text.
 * twMerge drops the conflicting base class instead.
 *
 * @param {object}   props
 * @param {Array}    props.columns            Column descriptors, rendered in order. Each:
 *   {
 *     key: string,                 // unique key; also the data field when no render()
 *     header: React.ReactNode,     // header cell content
 *     align?: 'left'|'right'|'center', // text alignment (default 'left')
 *     headerClassName?: string,    // extra classes on the <th>
 *     cellClassName?: string,      // extra classes on the <td>
 *     render?: (row, rowIndex) => React.ReactNode, // custom cell renderer
 *   }
 * @param {Array}    props.data               Row objects.
 * @param {Function|string} [props.rowKey]    Row key: a field name, or (row, i) => key. Defaults to row.id ?? index.
 * @param {boolean}  [props.loading=false]    Show the loading spinner instead of the table body.
 * @param {React.ReactNode} [props.emptyMessage='No records found.'] Shown when data is empty.
 * @param {React.ReactNode} [props.header]    Optional content for the card header strip.
 * @param {Function} [props.onRowClick]       Optional (row, rowIndex) => void; makes rows clickable.
 * @param {Function} [props.getRowProps]      Optional (row, rowIndex) => object spread onto each <tr> (e.g. data-* attributes).
 */
const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' };

const CTable = ({
  columns = [],
  data = [],
  rowKey,
  loading = false,
  emptyMessage = 'No records found.',
  header,
  onRowClick,
  getRowProps,
}) => {
  const getRowKey = (row, index) => {
    if (typeof rowKey === 'function') return rowKey(row, index);
    if (typeof rowKey === 'string') return row[rowKey];
    return row?.id ?? index;
  };

  // `data = []` only defaults on undefined, so a `data={null}` (an endpoint
  // returning { data: null }) reached data.length and blanked the page. Several
  // callers assign res.data.data straight through with no fallback.
  const rows = Array.isArray(data) ? data : [];

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500 text-sm">
          {emptyMessage}
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={twMerge(
                    'px-4 py-3 whitespace-nowrap font-medium text-gray-500',
                    ALIGN[col.align] || ALIGN.left,
                    col.headerClassName
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {rows.map((row, rowIndex) => {
              const { className: customClassName, ...restRowProps } =
                getRowProps ? getRowProps(row, rowIndex) : {};
              return (
                <tr
                  key={getRowKey(row, rowIndex)}
                  className={twMerge(
                    'hover:bg-gray-50',
                    onRowClick && 'cursor-pointer',
                    customClassName
                  )}
                  onClick={
                    onRowClick ? () => onRowClick(row, rowIndex) : undefined
                  }
                  {...restRowProps}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={twMerge(
                        'px-4 py-3 whitespace-nowrap text-gray-900',
                        ALIGN[col.align],
                        col.cellClassName
                      )}
                    >
                      {col.render ? col.render(row, rowIndex) : row[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-soft overflow-hidden">
      {header && (
        <div className="px-6 py-4 border-b border-gray-200">{header}</div>
      )}
      {renderBody()}
    </div>
  );
};

export default CTable;
